import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { Avatar, Box, IconButton, Link, List, ListItem, Popover, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Order } from 'src/enums';
import { getImageURL } from 'src/helpers';
import { useGetCatalogQuery } from 'src/redux/services/catalog';
import { ICatalogClient } from 'src/redux/services/clients';
import {
  useAddFavoriteClientMutation,
  useDeleteFavoriteClientMutation,
} from 'src/redux/services/users';
import { RootState } from 'src/redux/store';
import styles from './Catalog.module.css';
import { CustomTypography } from './custom/CustomTypography';
import { useGetCatalogEnabledQuery } from 'src/redux/services/settings';

export const Catalog: FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [groupedClients, setGroupedClients] = useState<Record<string, ICatalogClient[]>>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { id } = useSelector((state: RootState) => state.user.profile);
  const { data: catalogEnabled } = useGetCatalogEnabledQuery();

  const { data: catalogData } = useGetCatalogQuery(
    {
      sortBy: 'created_at',
      sortDirection: Order.DESC,
      limit: '',
      offset: 0,
      search: '',
    },
    { skip: !catalogEnabled },
  );

  useEffect(() => {
    const grouped = (catalogData?.items || []).reduce((acc, client) => {
      const typeName = client.type?.name || 'Прочее';

      if (!acc[typeName]) {
        acc[typeName] = [];
      }

      acc[typeName].push(client);
      return acc;
    }, {} as Record<string, ICatalogClient[]>);
    setGroupedClients(grouped);
  }, [catalogData]);

  const handleOpen = () => {
    if (buttonRef.current) {
      setAnchorEl(buttonRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!catalogEnabled) return <></>;

  return (
    <>
      <div className={styles.wrapper}>
        <div>
          <CustomTypography color="grey" className="text-12">
            Ресурсы
          </CustomTypography>
          <div className={styles.favoriteItems}>
            {catalogData?.items
              .filter((item) => item.favorite)
              .map((item) => (
                <div key={item.client_id}>
                  <Tooltip placement="right" title={item.name}>
                    <IconButton>
                      {item.avatar ? (
                        <Avatar
                          src={getImageURL(item.avatar)}
                          className={styles.clientAvatar}
                          alt={item.name}
                        />
                      ) : (
                        <LayersOutlinedIcon
                          className={clsx(styles.clientAvatarDefault, styles.clientAvatar)}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
          </div>
        </div>
        <Tooltip placement="right" title={'Каталог приложений'}>
          <Button
            className={styles.catalogButton}
            startIcon={<BookmarksOutlinedIcon />}
            onClick={handleOpen}
            ref={buttonRef}
          />
        </Tooltip>
      </div>

      <CatalogPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        groupedClients={groupedClients}
        userId={id}
      />
    </>
  );
};

interface ICatalogPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  groupedClients: Record<string, ICatalogClient[]>;
  userId?: string;
}

const CatalogPopover = ({ anchorEl, onClose, groupedClients, userId }: ICatalogPopoverProps) => {
  const isOpen = Boolean(anchorEl);
  const ContentRef = useRef<HTMLDivElement>(null);

  const [addFavorite] = useAddFavoriteClientMutation();
  const [deleteFavorite] = useDeleteFavoriteClientMutation();

  const handleAddFavorite = async (clientId: string) => {
    await addFavorite({ userId: userId || '', clientId }).unwrap();
  };

  const handleDeleteFavorite = async (clientId: string) => {
    await deleteFavorite({ userId: userId || '', clientId }).unwrap();
  };

  return (
    <>
      <Popover
        id="app-catalog"
        anchorEl={anchorEl}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClick={() => onClose()}
      >
        <Box className={styles.popover} ref={ContentRef}>
          <div className={styles.popoverHeader}>
            <Typography className={styles.popoverTitle}>Каталог</Typography>
            <IconButton className={styles.popoverButton} onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </div>
          <div className={styles.popoverContent}>
            {Object.keys(groupedClients).length === 0 && (
              <Typography>Нет доступных сервисов</Typography>
            )}
            {Object.keys(groupedClients).map((typeName) => (
              <div key={typeName}>
                <CustomTypography
                  key={`${typeName}-title`}
                  className={clsx('text-14', styles.groupTitle)}
                  color="grey"
                >
                  {typeName}
                </CustomTypography>
                <List key={`${typeName}-list`} className={styles.popoverList}>
                  {groupedClients[typeName].map((client) => (
                    <ListItem key={client.client_id} className={styles.popoverItem}>
                      <Tooltip placement="bottom" title={client.name}>
                        <div className={styles.clientAvatar}>
                          <Link href={client.domain} target="_blank">
                            {client.avatar ? (
                              <Avatar
                                src={getImageURL(client.avatar)}
                                alt={client.name}
                                className={styles.clientAvatar}
                              />
                            ) : (
                              <LayersOutlinedIcon
                                className={clsx(styles.clientAvatarDefault, styles.clientAvatar)}
                              />
                            )}
                          </Link>
                          {client.favorite ? (
                            <Button
                              className={styles.clientButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteFavorite(client.client_id);
                              }}
                              startIcon={
                                <BookmarkAddedIcon className={styles.clientButtonIconActive} />
                              }
                            />
                          ) : (
                            <Button
                              className={styles.clientButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleAddFavorite(client.client_id);
                              }}
                              startIcon={
                                <BookmarkAddOutlinedIcon className={styles.clientButtonIcon} />
                              }
                            />
                          )}
                        </div>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              </div>
            ))}
          </div>
        </Box>
      </Popover>
    </>
  );
};
